import { css } from '@flex-design-system/stitches-react';
import { getFlexicalTheme } from '@flex-services/flexical';
import type { FlexicalThemeOptions } from '@flex-services/flexical';

// Flexical 기본 테마의 블록 스타일은 유지하고, 상하 여백과 줄 높이만
// 블록 스타일이 바뀌기 전(@flex-services/flexical 1.504.0)의 값으로 덮어쓴다.
// - 상하 여백: 이전에는 문단·제목·목록·인용 모두 0이었다.
// - 줄 높이: 본문은 글자 크기의 1.5배, 제목은 33px·28px·22px였다.
// 기본 테마의 클래스는 variant 클래스와 `:first-child` 같은 선택자로 지정되므로
// 덮어쓰는 규칙은 클래스를 세 번 반복해 우선순위를 높인다.
// https://flex-cv82520.slack.com/archives/C084SQV4C3Z/p1793237911497979?thread_ts=1792995254.134859&cid=C084SQV4C3Z

type BaseFontSize = NonNullable<FlexicalThemeOptions['baseFontSize']>;

const BODY_LINE_HEIGHT_RATIO = 1.5;

const noVerticalPadding = css({
  '&&&': { paddingTop: 0, paddingBottom: 0 },
});

const heading1 = css({ '&&&': { lineHeight: '33px' } });
const heading2 = css({ '&&&': { lineHeight: '28px' } });
const heading3 = css({ '&&&': { lineHeight: '22px' } });

const body = css({
  variants: {
    baseFontSize: {
      14: { '&&&': { lineHeight: `${14 * BODY_LINE_HEIGHT_RATIO}px` } },
      16: { '&&&': { lineHeight: `${16 * BODY_LINE_HEIGHT_RATIO}px` } },
    },
  },
});

// 기본 테마의 목록 마커는 항목 위 여백만큼 내려 그리므로 여백을 없앤 만큼 마커도 올린다.
// 마커는 본문 한 줄 높이 안에서 가운데 정렬하므로 높이도 본문 줄 높이에 맞춘다.
const listItem = css({
  variants: {
    baseFontSize: {
      14: {
        '&&&::before': {
          top: 0,
          height: 14 * BODY_LINE_HEIGHT_RATIO,
          lineHeight: `${14 * BODY_LINE_HEIGHT_RATIO}px`,
        },
      },
      16: {
        '&&&::before': {
          top: 0,
          height: 16 * BODY_LINE_HEIGHT_RATIO,
          lineHeight: `${16 * BODY_LINE_HEIGHT_RATIO}px`,
        },
      },
    },
  },
});

// 이전 인용 막대는 본문보다 위아래로 3px씩 길게 그렸다.
const quote = css({
  '&&&::before': { top: -3, bottom: -3 },
});

const joinClassNames = (...classNames: (string | undefined)[]) => {
  return classNames.filter(Boolean).join(' ');
};

/**
 * 채용에서 쓰는 Flexical 테마를 반환합니다.
 * 기본 테마에 상하 여백과 줄 높이를 이전 값으로 덮어쓰는 클래스를 더합니다.
 * @param options 기본 테마를 만드는 옵션입니다.
 */
export const getRecruitingFlexicalTheme = (options: {
  baseFontSize: BaseFontSize;
}) => {
  const defaultTheme = getFlexicalTheme(options);
  const variants = { baseFontSize: options.baseFontSize };
  const bodyClassName = joinClassNames(
    noVerticalPadding().className,
    body(variants).className
  );

  return {
    ...defaultTheme,
    root: joinClassNames(defaultTheme.root, body(variants).className),
    paragraph: joinClassNames(defaultTheme.paragraph, bodyClassName),
    placeholder: joinClassNames(defaultTheme.placeholder, bodyClassName),
    heading: {
      ...defaultTheme.heading,
      h1: joinClassNames(
        defaultTheme.heading?.h1,
        noVerticalPadding().className,
        heading1().className
      ),
      h2: joinClassNames(
        defaultTheme.heading?.h2,
        noVerticalPadding().className,
        heading2().className
      ),
      h3: joinClassNames(
        defaultTheme.heading?.h3,
        noVerticalPadding().className,
        heading3().className
      ),
    },
    list: {
      ...defaultTheme.list,
      ol: joinClassNames(defaultTheme.list?.ol, noVerticalPadding().className),
      ul: joinClassNames(defaultTheme.list?.ul, noVerticalPadding().className),
      listitem: joinClassNames(
        defaultTheme.list?.listitem,
        bodyClassName,
        listItem(variants).className
      ),
    },
    quote: joinClassNames(defaultTheme.quote, bodyClassName, quote().className),
  };
};
